import { useState, useEffect } from 'react';
import { ArrowLeft, HelpCircle, MessageCircle, Mail, ChevronDown, ChevronUp, Send, Loader2 } from 'lucide-react';
import { useResponsive } from '../../hooks/useResponsive';
import { FAQ_DATA } from '../../utils/constants';
import { wixFAQApi } from '../../services/api';

export default function SupportCenter({ onBack }) {
  const { isDesktop } = useResponsive();
  const [activeSection, setActiveSection] = useState(null); // 'faq' | 'chat' | 'email'
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqData, setFaqData] = useState(FAQ_DATA); // Start with static data as fallback
  const [faqLoading, setFaqLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { id: 1, sender: 'ai', text: "Hi! I'm your Bonndle AI assistant. How can I help you today?" },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [emailSent, setEmailSent] = useState(false);

  // Fetch FAQ from API when FAQ section is opened
  useEffect(() => {
    if (activeSection === 'faq') {
      fetchFAQ();
    }
  }, [activeSection]);

  const fetchFAQ = async () => {
    setFaqLoading(true);
    try {
      const items = await wixFAQApi.getFAQ();
      if (items && items.length > 0) {
        setFaqData(items);
      }
      // If no items returned, keep using static FAQ_DATA
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
      // Keep using static FAQ_DATA on error
    } finally {
      setFaqLoading(false);
    }
  };

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    setChatMessages((prev) => [
      ...prev,
      { id: Date.now(), sender: 'user', text: chatInput },
      { id: Date.now() + 1, sender: 'ai', text: "Thanks for your message! I'm looking into that for you." },
    ]);
    setChatInput('');
  };

  const handleSendEmail = () => {
    if (!emailForm.subject || !emailForm.message) return;
    setEmailSent(true);
  };

  // FAQ Section
  if (activeSection === 'faq') {
    return (
      <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50">
        <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
          <button onClick={() => setActiveSection(null)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">FAQs</h1>
        </header>
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
          {faqLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100">
              {faqData.map((item, idx) => (
                <div key={item.id || idx} className="p-4">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <span className="font-medium text-gray-900">{item.q}</span>
                    {expandedFaq === idx ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </button>
                  {expandedFaq === idx && (
                    <p className="mt-3 text-sm text-gray-600">{item.a}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Chat Section
  if (activeSection === 'chat') {
    return (
      <div className="flex-1 flex flex-col bg-gray-50">
        <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
          <button onClick={() => setActiveSection(null)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">AI Support</h1>
        </header>
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDesktop ? 'max-w-4xl mx-auto w-full' : ''}`}>
          {chatMessages.map((msg) => (
            <div
              key={msg.id}
              className={`max-w-[80%] p-3 rounded-2xl ${
                msg.sender === 'user'
                  ? 'ml-auto bg-indigo-600 text-white'
                  : 'bg-white text-gray-900 shadow-sm'
              }`}
            >
              {msg.text}
            </div>
          ))}
        </div>
        <div className="bg-white p-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleSendChat}
              className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Email Section
  if (activeSection === 'email') {
    return (
      <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50">
        <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
          <button onClick={() => setActiveSection(null)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">Send Email</h1>
        </header>
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
          {emailSent ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="text-emerald-600" size={28} />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Email Sent!</h2>
              <p className="text-gray-500">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="What's this about?"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Describe your issue or question..."
                  rows={6}
                  className="input resize-none"
                />
              </div>
              <button
                onClick={handleSendEmail}
                className="w-full py-4 bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700"
              >
                Send Email
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main Support Menu
  return (
    <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50">
      <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
        <button onClick={onBack} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold text-gray-900">Support Center</h1>
      </header>
      <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        <div className="space-y-4">
          <button
            onClick={() => setActiveSection('faq')}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left"
          >
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
              <HelpCircle className="text-indigo-600" size={24} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Frequently Asked Questions</p>
              <p className="text-sm text-gray-500">Find quick answers</p>
            </div>
          </button>

          <button
            onClick={() => setActiveSection('chat')}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Chat with AI Support</p>
              <p className="text-sm text-gray-500">Get instant help</p>
            </div>
          </button>

          <button
            onClick={() => setActiveSection('email')}
            className="w-full flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow text-left"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Mail className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Send Email</p>
              <p className="text-sm text-gray-500">We'll respond within 24h</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
